/**
 * Simple Lazy Loading Wrapper
 * Wraps existing img tags with lazy loading behavior
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

interface LazyLoadProps {
  children: ReactNode;
  className?: string;
  rootMargin?: string;
  placeholder?: ReactNode;
  onVisible?: () => void;
}

/**
 * LazyLoad wrapper component
 * Use this to wrap any content for lazy loading
 */
export function LazyLoad({
  children,
  className = '',
  rootMargin = '50px',
  placeholder,
  onVisible
}: LazyLoadProps) {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
            onVisible?.();
            observer.disconnect();
          }
        });
      },
      { rootMargin, threshold: 0.01 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible, rootMargin, onVisible]);

  return (
    <div ref={elementRef} className={className}>
      {isVisible ? children : placeholder || <div className="w-full h-full bg-gray-200 animate-pulse" />}
    </div>
  );
}

/**
 * Add loading="lazy" to all images in the app
 * Call this utility function to automatically add lazy loading to existing images
 */
export function enableLazyLoadingOnExistingImages() {
  if (typeof window === 'undefined') return;

  // Find all images without loading attribute
  const images = document.querySelectorAll('img:not([loading])');
  
  images.forEach((img) => {
    img.setAttribute('loading', 'lazy');
    img.setAttribute('decoding', 'async');
  });
}

/**
 * Optimize background images with lazy loading
 */
export function useLazyBackgroundImage(imageUrl: string, rootMargin = '50px') {
  const [shouldLoad, setShouldLoad] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!elementRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin, threshold: 0.01 }
    );

    observer.observe(elementRef.current);

    return () => observer.disconnect();
  }, [rootMargin]);

  return {
    ref: elementRef,
    style: shouldLoad ? { backgroundImage: `url(${imageUrl})` } : {}
  };
}

export default LazyLoad;
